import structureTypes from 'common/constants/structureTypes'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import EntityInterface, {
  EntityUnionType,
} from 'common/types/entities/EntityInterface'
import {
  OldEntityInterface,
  OldFieldEntityInterface,
  OldTextEntityInterface,
} from 'common/types/entities/OldEntityInterface'
import { PopupInterface } from 'common/types/entities/popup-interface'
import { EntityType } from 'common/types/Page'
import * as entityUtils from 'common/utils/entityUtils'
import { Entities, getEntityAncestorByType } from 'common/utils/entityUtils'
import { CheckboxEntityInterface } from 'src/shared/entities/checkbox/checkbox-entity.schema'
import { FieldEntityInterface } from 'src/shared/entities/field/field-entity.schema'
import { EntityIdType } from 'src/shared/schemas/entity-id.schema'
import { isWebsitePage } from 'src/shared/utils/is-website-page'
import { FieldSlugEnum } from '../../../common/enums/FieldSlugEnum'
import getRootEntityTypeByPageType from '../../utils/getRootEntityTypeByPageType'
import { PageState } from './PageStateInterface'

function getLocale(pageState: PageState) {
  return pageState.locale
}

function getGlobalLinkColor(state: PageState) {
  return state.globalSettings?.linkColor
}

function getGlobalTextColor(state: PageState) {
  return state.globalSettings?.textColor
}

function getGlobalTextFontFamily(state: PageState) {
  if (state.globalSettings?.fontFamily) {
    return state.globalSettings.fontFamily
  }
}

function getGlobalTextFontSize(state: PageState) {
  if (state.globalSettings?.textFontSize) {
    return Number.parseInt(state.globalSettings.textFontSize, 10)
  }
}

export function getTaxNumberFieldEntity(state: PageState) {
  return Object.values(state.entities).find(
    entity =>
      (entity.type === structureTypes.FORM_INPUT &&
        (entity as OldEntityInterface).options.slug ===
          FieldSlugEnum.TaxNumber) ||
      (entity.type === EntityTypeEnum.Field &&
        (entity as FieldEntityInterface).slug === FieldSlugEnum.TaxNumber),
  )
}

export function getCountryTypeEntity(state: PageState) {
  return Object.values(state.entities).find(
    entity =>
      (entity.type === structureTypes.FORM_INPUT &&
        (entity as OldEntityInterface).options.slug ===
          FieldSlugEnum.Country) ||
      (entity.type === EntityTypeEnum.Field &&
        (entity as FieldEntityInterface).slug === FieldSlugEnum.Country),
  )
}

export const getChildrenEntities = (state: PageState, childIds: string[]) =>
  childIds.map(childId => state.entities[childId]).filter(Boolean)

function getGlobalMobileTextFontSize(state: PageState) {
  if (state.globalSettings?.mobileTextFontSize) {
    return Number.parseInt(state.globalSettings.mobileTextFontSize, 10)
  }
}

function getGlobalTextLineHeight(state: PageState) {
  if (state.globalSettings?.textLineHeight) {
    return Number.parseInt(state.globalSettings.textLineHeight, 10)
  }
}

function getGlobalMobileTextLineHeight(state: PageState) {
  if (state.globalSettings?.mobileTextLineHeight) {
    return Number.parseInt(state.globalSettings.mobileTextLineHeight, 10)
  }
}

function getOrderedByPositionHeadings(state: PageState, isMobile: boolean) {
  return entityUtils.getOrderedByPositionHeadings(
    state.entities,
    isMobile,
    state.type,
  ) as OldTextEntityInterface[]
}

function getGlobalHeadingColor(state: PageState) {
  return state.globalSettings?.headingColor
}

function getGlobalTextAlign(state: PageState) {
  return state.globalSettings?.textAlign || 'center'
}

function getGlobalMobileTextAlign(state: PageState) {
  return state.globalSettings?.mobileTextAlign || getGlobalTextAlign(state)
}

function getGlobalHeadingAlign(state: PageState) {
  return state.globalSettings?.headingAlign || 'center'
}

function getGlobalMobileHeadingAlign(state: PageState) {
  return (
    state.globalSettings?.mobileHeadingAlign || getGlobalHeadingAlign(state)
  )
}

function getGlobalHeadingFontFamily(state: PageState) {
  if (state.globalSettings?.headingFontFamily) {
    return state.globalSettings.headingFontFamily
  }
}

function getGlobalFontProperties({ globalSettings }: PageState) {
  return {
    fontFamily: globalSettings?.fontFamily,
    fontStyle: globalSettings?.fontStyle,
    fontWeight: globalSettings?.fontWeight,
    headingFontFamily: globalSettings?.headingFontFamily,
    headingFontStyle: globalSettings?.headingFontStyle,
    headingFontWeight: globalSettings?.headingFontWeight,
  }
}

function getGlobalScrollPaddingTop({ globalSettings }: PageState) {
  return globalSettings ? globalSettings.scrollPaddingTop : null
}

function getSeo(pageState: PageState) {
  return pageState.seo
}

export function getSeoSocialImageFileId({ seo }: PageState) {
  if (
    typeof seo.socialImage === 'string' &&
    !Number.isNaN(Number(seo.socialImage))
  ) {
    return parseInt(seo.socialImage, 10)
  }

  if (typeof seo.socialImage === 'number') {
    return seo.socialImage
  }
}

export function getPageType(state: PageState) {
  return state.type
}

function getRootEntity(state: PageState) {
  return Object.values(state.entities).find(entity =>
    getRootEntityTypeByPageType(state.type).includes(
      entity.type as EntityTypeEnum,
    ),
  )
}

function hasAllVisibleAscendants(
  state: PageState,
  entity: OldEntityInterface | EntityInterface,
  isDesktop: boolean,
) {
  return entityUtils.hasAllVisibleAscendants(entity, state.entities, isDesktop)
}

export function getFirstEntityByType(
  state: PageState,
  type: EntityTypeEnum | keyof typeof structureTypes,
) {
  return Object.values(state.entities).find(entity => entity.type === type)
}

export function getPageId(state: PageState) {
  return state.id
}

function getAscendantPopupId(
  state: PageState,
  entity: OldEntityInterface | EntityInterface,
) {
  const popup = getEntityAncestorByType(
    state.entities,
    entity,
    EntityTypeEnum.Popup,
  )

  return popup ? popup.id : null
}

export const getAscendantPopup = (
  { entities }: PageState,
  entity: OldEntityInterface | EntityInterface,
) => {
  return getEntityAncestorByType(entities, entity, EntityTypeEnum.Popup)
}

function getVisibleCheckboxes(
  state: PageState,
  isDesktop: boolean,
  popupId?: string | null,
) {
  return Object.values(state.entities)
    .filter(entity => entity.type === EntityTypeEnum.Checkbox)
    .filter(
      entity =>
        entityUtils.isVisible(entity, isDesktop) &&
        entityUtils.hasAllVisibleAscendants(entity, state.entities, isDesktop),
    )
    .filter(entity =>
      popupId
        ? entityUtils.isPopupAscendantOfEntity(
            state.entities[popupId],
            entity,
            state.entities,
          )
        : entityUtils.isNotBelongsToPopups(entity, state.entities),
    )
}

export const getVisibleMandatoryCheckboxes = (
  state: PageState,
  isDesktop: boolean,
  popupId?: string | null,
): CheckboxEntityInterface[] => {
  return Object.values(state.entities)
    .filter(
      (entity): entity is CheckboxEntityInterface =>
        entity.type === EntityTypeEnum.Checkbox,
    )
    .filter(
      entity =>
        entityUtils.isVisible(entity, isDesktop) &&
        entityUtils.hasAllVisibleAscendants(entity, state.entities, isDesktop),
    )
    .filter(entity =>
      popupId
        ? entityUtils.isPopupAscendantOfEntity(
            state.entities[popupId],
            entity,
            state.entities,
          )
        : entityUtils.isNotBelongsToPopups(entity, state.entities),
    )
    .filter(entity => !entity?.optional) as CheckboxEntityInterface[]
}

function getVisibleProductElement(state: PageState, isDesktop: boolean) {
  return Object.values(state.entities)
    .filter(entity => entity.type === EntityTypeEnum.Product)
    .filter(
      entity =>
        entityUtils.isVisible(entity, isDesktop) &&
        entityUtils.hasAllVisibleAscendants(entity, state.entities, isDesktop),
    )
}

function getVisibleTwoStepPaymentFormPaymentStep(
  state: PageState,
  isDesktop: boolean,
) {
  return Object.values(state.entities).find(
    entity =>
      entity.type === EntityTypeEnum.TwoStepPaymentFormStepPayment &&
      entityUtils.isVisible(entity, isDesktop) &&
      entityUtils.hasAllVisibleAscendants(entity, state.entities, isDesktop),
  )
}

export const getVisibleOptInFieldProperties = (
  state: PageState,
  isDesktop: boolean,
  fields: Record<string, string>,
  popupId?: string | null,
) => {
  return Object.values(state.entities)
    .filter(
      (entity): entity is OldFieldEntityInterface | FieldEntityInterface =>
        entity.type === structureTypes.FORM_INPUT ||
        entity.type === EntityTypeEnum.Field,
    )
    .filter(entity => {
      if (entity.type === structureTypes.FORM_INPUT) {
        return (
          (entity as OldFieldEntityInterface).options.slug !== null &&
          (entity as OldFieldEntityInterface).options.slug !== undefined
        )
      } else if (entity.type === EntityTypeEnum.Field) {
        return entity.slug !== null && entity.slug !== undefined
      }

      return false
    })
    .filter(
      entity =>
        entityUtils.isVisible(entity, isDesktop) &&
        entityUtils.hasAllVisibleAscendants(entity, state.entities, isDesktop),
    )
    .filter(entity =>
      popupId
        ? entityUtils.isPopupAscendantOfEntity(
            state.entities[popupId],
            entity,
            state.entities,
          )
        : entityUtils.isNotBelongsToPopups(entity, state.entities),
    )
    .reduce(
      (acc, entity) => {
        if (entity.type === structureTypes.FORM_INPUT) {
          const castedSlug = (entity as OldFieldEntityInterface).options
            .slug as string
          const isMandatory =
            acc[castedSlug] && acc[castedSlug].optional === false

          acc[castedSlug] = {
            value: fields[castedSlug],
            optional: isMandatory
              ? false
              : !!(entity as OldFieldEntityInterface).options.optional,
          }
        } else if (entity.type === EntityTypeEnum.Field) {
          if (!entity.slug) {
            return acc
          }

          const isMandatory =
            acc[entity.slug] && acc[entity.slug].optional === false

          acc[entity.slug] = {
            value: fields[entity.slug],
            optional: isMandatory ? false : entity.optional,
          }
        }

        return acc
      },
      {} as Record<string, { value: string; optional: boolean }>,
    )
}

export const findEntityDescendantsCheckboxes = (
  state: PageState,
  entityId: EntityIdType,
): Entities => {
  const mandatoryCheckboxes = entityUtils
    .getAllDescendantsByEntityType(
      state.entities,
      entityId,
      EntityTypeEnum.Checkbox,
    )
    .filter(checkbox => !(checkbox as CheckboxEntityInterface).optional)

  return mandatoryCheckboxes
}

function getEntitiesByType(pageState: PageState, type: EntityType) {
  return Object.values(pageState.entities).filter(
    entity => entity.type === type,
  )
}

export const getVisibleMandatoryOptInRecaptchas = (
  state: PageState,
  isDesktop: boolean,
  popupId?: string | null,
) => {
  return Object.values(state.entities)
    .filter(entity => entity.type === EntityTypeEnum.OptInRecaptcha)
    .filter(
      entity =>
        entityUtils.isVisible(entity, isDesktop) &&
        entityUtils.hasAllVisibleAscendants(entity, state.entities, isDesktop),
    )
    .filter(entity =>
      popupId
        ? entityUtils.isPopupAscendantOfEntity(
            state.entities[popupId],
            entity,
            state.entities,
          )
        : entityUtils.isNotBelongsToPopups(entity, state.entities),
    )
}

export const getEntityDescendantsContactUsElements = (
  state: PageState,
  entityId: EntityIdType,
) => {
  const contactUsFields = entityUtils.getAllDescendantsByEntityType(
    state.entities,
    entityId,
    EntityTypeEnum.ContactUsField,
  )
  const oldContactUsRecaptcha = entityUtils.getAllDescendantsByEntityType(
    state.entities,
    entityId,
    EntityTypeEnum.Recaptcha,
  )
  const contactUsRecaptcha = entityUtils.getAllDescendantsByEntityType(
    state.entities,
    entityId,
    EntityTypeEnum.ContactUsRecaptcha,
  )
  const contactUsAttachments = entityUtils.getAllDescendantsByEntityType(
    state.entities,
    entityId,
    EntityTypeEnum.Attachments,
  )
  const contactUsButton = entityUtils.getAllDescendantsByEntityType(
    state.entities,
    entityId,
    EntityTypeEnum.Button,
  )
  return contactUsFields.concat(
    oldContactUsRecaptcha,
    contactUsRecaptcha,
    contactUsAttachments,
    contactUsButton,
  )
}

export function getIsPageTemplate(state: PageState) {
  return state.isTemplate
}

export const getIsWebsitePageType = (pageState: PageState) => {
  return isWebsitePage(pageState.type)
}

const getWebsiteGlobalPopupEntities = (pageState: PageState) => {
  const popupEntities = Object.values(pageState.entities).filter(
    entity =>
      entity.type === EntityTypeEnum.Popup &&
      'isWebsiteGlobal' in entity &&
      entity.isWebsiteGlobal,
  ) as PopupInterface[]
  return popupEntities
}

const getPagePopupEntities = (pageState: PageState) => {
  const popupEntities = Object.values(pageState.entities).filter(
    entity =>
      entity.type === EntityTypeEnum.Popup &&
      !('isWebsiteGlobal' in entity && entity.isWebsiteGlobal),
  ) as PopupInterface[]
  return popupEntities
}

/**
 * NOTE: we use website global popups only inside a website header and a website footer,
 * and use page popups only inside a "page body"
 */
const getWebsiteGlobalOrPagePopupEntities =
  (pageState: PageState) => (entity: EntityUnionType) => {
    if (getIsWebsitePageType(pageState)) {
      if (
        getEntityAncestorByType(
          pageState.entities,
          entity,
          EntityTypeEnum.WebsiteHeader,
        ) ||
        getEntityAncestorByType(
          pageState.entities,
          entity,
          EntityTypeEnum.WebsiteFooter,
        )
      ) {
        return getWebsiteGlobalPopupEntities(pageState)
      } else {
        return getPagePopupEntities(pageState)
      }
    } else {
      return getPopupEntities(pageState)
    }
  }

/**
 * NOTE: use the getWebsiteGlobalOrPagePopupEntities instead if it's possible
 */
export function getPopupEntities(state: PageState) {
  return Object.values(state.entities).filter(
    entity => entity.type === EntityTypeEnum.Popup,
  ) as PopupInterface[]
}

export const getFirstOnExitPopup = ({ entities }: PageState) =>
  Object.values(entities).find(
    entity =>
      entity.type === EntityTypeEnum.Popup &&
      (entity as PopupInterface)?.openOnExit,
  )

export const getEntityById = (state: PageState, entityId: string | null) => {
  if (!entityId) {
    return null
  }
  return state.entities[entityId] || null
}

export const getOnloadPopups = ({ entities }: PageState) =>
  Object.values(entities).filter(
    entity =>
      entity.type === EntityTypeEnum.Popup &&
      (entity as PopupInterface)?.openAutomatically,
  )

export const getMobileOnloadPopups = ({ entities }: PageState) =>
  Object.values(entities).filter(
    entity =>
      entity.type === EntityTypeEnum.Popup &&
      (entity as PopupInterface)?.openOnMobileAutomatically,
  )

const selectors = {
  getPageId,
  getLocale,
  getGlobalLinkColor,
  getGlobalTextColor,
  getGlobalTextFontSize,
  getGlobalTextFontFamily,
  getGlobalMobileTextFontSize,
  getGlobalTextLineHeight,
  getGlobalMobileTextLineHeight,
  getOrderedByPositionHeadings,
  getGlobalHeadingColor,
  getGlobalHeadingFontFamily,
  getGlobalFontProperties,
  getPageType,
  hasAllVisibleAscendants,
  getRootEntity,
  getSeo,
  getAscendantPopupId,
  getVisibleCheckboxes,
  getVisibleProductElement,
  getVisibleOptInFieldProperties,
  getVisibleMandatoryCheckboxes,
  findEntityDescendantsCheckboxes,
  getEntitiesByType,
  getVisibleMandatoryOptInRecaptchas,
  getEntityDescendantsContactUsElements,
  getIsPageTemplate,
  getChildrenEntities,
  getGlobalScrollPaddingTop,
  getFirstEntityByType,
  getWebsiteGlobalPopupEntities,
  getPagePopupEntities,
  getWebsiteGlobalOrPagePopupEntities,
  getPopupEntities,
  getFirstOnExitPopup,
  getEntityById,
  getOnloadPopups,
  getMobileOnloadPopups,
  getGlobalTextAlign,
  getGlobalMobileTextAlign,
  getGlobalHeadingAlign,
  getGlobalMobileHeadingAlign,
  getVisibleTwoStepPaymentFormPaymentStep,
}

export default selectors
